"use client";
import React, { useState } from "react";

import IMAGES from "@/data/assetData";
import { COMPANY } from "@/data/company";
import Link from "next/link";
import { usefetchDataFromApi } from "@/utils/api";
import handleApiError from "@/utils/handleApiError";
import { useForm } from "react-hook-form";
import Footer from "@/components/Footer/Footer";
import Header from "@/components/Header/Header";
import Image from "next/image";
import MainBreadcrumb from "@/components/Breadcrumb/MainBreadcrumb";

export default function ListVehicle() {
  const [partnerStatus, setPartnerStatus] = useState("");
  const [partnerError, setPartnerError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const handlePartner = (data) => {
    setPartnerError(false);
    setPartnerStatus("");
    usefetchDataFromApi({
      url: "site/become-partner",
      method: "POST",
      data: {
        name: data.fullname,
        mobile: data.phoneNumber,
        vehicleCount: data.vehicleCount,
        address: data.city,
        country: data.country,
        platform: 3,
      },
    })
      .then((res) => {
        if (res.success) {
          setPartnerStatus(res.message || "Request submitted successfully");
          setPartnerError(false);
          reset();
        } else {
          setPartnerError(true);
          setPartnerStatus(res.message || "Something went wrong. Please try again");
        }
        setTimeout(() => {
          setPartnerStatus("");
          setPartnerError(false);
        }, 3000);
      })
      .catch((err) => {
        setPartnerError(true);
        setPartnerStatus(handleApiError(err));
      });
  };

  return (
    <>
      <Header />
      <main>
      <div className="mt-[74px] max-lg:mt-[62px]">
        <MainBreadcrumb variant="static" seoAllData={[{ name: "Become Partner", url: "/become-partner/" }]} />
      </div>
      <div className="lg:hidden min-h-12 px-4 fixed top-[14px] left-0 bg-white border-b border-gray-300 w-full shadow-md flex space-x-5 items-center text-black z-20">
        <Link href="/">
          <Image
            src={IMAGES.Chevron_icon}
            alt="Back"
            height={18}
            width={18}
          />
        </Link>
        <span className="text-base font-medium lg:text-lg leading-[27px] text-black">
          List your Vehicle
        </span>
      </div>

      <div className="w-full mt-4 mb-24 lg:mt-6 lg:mb-32">
        <div className="mx-[5%] lg:mx-[4%]">
          <div className="text-center">
            <h1 className="font-bold text-4xl text-amber-400 pt-0 lg:pt-6">
              Become Partner
            </h1>
            <p className="p-6 text-lg font-medium leading-[27px] text-gray-500">
            Sukuto is India&apos;s largest bike and car rental marketplace.<br /> As a tech-driven company, we prioritize innovative solutions while putting relationships first.<br /> Our platform is designed to provide seamless and efficient rental experiences, ensuring that our customers and partners receive the highest level of service and support.🙂
            </p>
          </div>

          {/* Below-the-fold content. Adds H2/H3 hierarchy (page previously had
              only H1 + Footer H3s, no H2), real onboarding context, and useful
              answers for prospective partners — improves SEO depth and conversion. */}
          <section className="max-w-screen-xl mx-auto mb-12 lg:mb-16" aria-labelledby="partner-why-heading">
            <h2 id="partner-why-heading" className="text-2xl lg:text-3xl font-semibold text-center mt-2 mb-3">
              Why partner with Sukuto
            </h2>
            <p className="text-center text-gray-600 text-base max-w-3xl mx-auto mb-10 leading-relaxed">
              Sukuto is a self-drive bike and car rental marketplace operating across 100+ Indian cities. List your fleet once and reach travellers, commuters and tourists searching for rentals in your city — without building your own booking system, payment stack or customer-acquisition pipeline.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <article className="rounded-xl border border-gray-200 p-6 bg-white text-left">
                <h3 className="text-lg font-semibold mb-2">Verified customers, every booking</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Every rider that books your fleet goes through Sukuto&apos;s ID and licence verification. You only meet pre-qualified customers at handover — no last-minute &quot;no driving licence&quot; cancellations at your hub.
                </p>
              </article>

              <article className="rounded-xl border border-gray-200 p-6 bg-white text-left">
                <h3 className="text-lg font-semibold mb-2">Bookings + payments handled</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Listings, search visibility, deposit collection, customer payments and refund handling all run through the Sukuto platform. Payouts arrive on a regular schedule. You focus on your bikes and cars; we run the rest of the funnel.
                </p>
              </article>

              <article className="rounded-xl border border-gray-200 p-6 bg-white text-left">
                <h3 className="text-lg font-semibold mb-2">City + locality discovery</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Sukuto runs dedicated SEO landing pages for every rental city and many sub-areas (airports, suburbs, tourist hubs). Your fleet shows up where renters are already searching — at the city level and inside the locality pages closest to your hub.
                </p>
              </article>
            </div>

            <h2 className="text-2xl lg:text-3xl font-semibold text-center mt-12 mb-3">
              How to onboard your fleet
            </h2>
            <p className="text-center text-gray-600 text-base max-w-3xl mx-auto mb-10 leading-relaxed">
              The onboarding process is built to get you live with minimum back-and-forth. Most partners complete it in under a week from first contact.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <article className="rounded-xl border border-gray-200 p-6 text-left">
                <h3 className="text-lg font-semibold mb-2">1. Submit the form above</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Send us your name, city, fleet size and contact number. We&apos;ll reach out to walk you through paperwork, payouts and listing setup tailored to your city.
                </p>
              </article>

              <article className="rounded-xl border border-gray-200 p-6 text-left">
                <h3 className="text-lg font-semibold mb-2">2. Share documents &amp; vehicles</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Standard business documents, RC and insurance details for each vehicle, vehicle photographs and your pickup-hub address. Our partner team verifies everything before anything goes live.
                </p>
              </article>

              <article className="rounded-xl border border-gray-200 p-6 text-left">
                <h3 className="text-lg font-semibold mb-2">3. Go live &amp; receive bookings</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Your fleet goes live on relevant city and locality pages. Bookings, customer chat, deposits and payouts run through the partner dashboard from day one.
                </p>
              </article>
            </div>

            <h2 className="text-2xl lg:text-3xl font-semibold text-center mt-12 mb-3">
              Who can partner with Sukuto
            </h2>
            <p className="text-center text-gray-600 text-base max-w-3xl mx-auto mb-10 leading-relaxed">
              Sukuto works with established rental businesses, vehicle owners with multi-vehicle fleets and self-drive operators looking to expand reach beyond their immediate locality.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <article className="rounded-xl border border-gray-200 p-6 text-left">
                <h3 className="text-lg font-semibold mb-2">Two-wheeler rental operators</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Scooters (Activa, Dio, Access, Jupiter), commuter bikes (Splendor, CT100), and adventure motorcycles (Royal Enfield Classic, Bullet, Himalayan; KTM Duke; Bajaj Avenger). Cities with strong tourist or commuter rental demand are highest priority for new listings.
                </p>
              </article>

              <article className="rounded-xl border border-gray-200 p-6 text-left">
                <h3 className="text-lg font-semibold mb-2">Self-drive car operators</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Hatchbacks, sedans and SUVs from Maruti Suzuki, Hyundai, Honda, Toyota, Mahindra, Tata and Ford. Self-drive car listings work well for weekend getaways, intercity trips and corporate clients.
                </p>
              </article>
            </div>

            <h2 className="text-2xl lg:text-3xl font-semibold text-center mt-12 mb-3">
              Cities we&apos;re actively expanding in
            </h2>
            <p className="text-center text-gray-600 text-base max-w-3xl mx-auto mb-2 leading-relaxed">
              Sukuto already operates in <strong>100+ Indian cities</strong> — including Bangalore, Mumbai, Delhi NCR, Pune, Goa, Hyderabad, Chennai, Kolkata, Jaipur, Manali, Rishikesh, Dharamshala, Pondicherry, Gokarna and many more. We&apos;re actively recruiting partners in airports, hill stations, tier-2 cities and tourist destinations where supply lags demand. If your hub is in or near these markets, we want to hear from you.
            </p>
          </section>

          <div className="">
            <div className="border md:p-6 p-3 rounded-xl">
              <div className="md:flex max-w-screen-xl mx-auto">
                <div
                  className="rounded-xl text-white md:p-6 p-3 partner md:w-[40%] w-auto bg-brownDark bg-contact-halficircle bg-no-repeat bg-right-bottom relative"
                >
                  <p className="capitalize font-semibold md:text-[28px] leading-[42px] mb-4">
                    get in touch
                  </p>
                  <ul>
                    <li className="flex mb-4 items-center">
                      <a href={COMPANY.mapsUrl} target="_blank" rel="noopener noreferrer" className="md:mb-5">
                        <Image
                          width={16}
                          height={21}
                          className="mr-5"
                          src={IMAGES.location}
                          alt="Location icon"
                        />
                      </a>
                      <p className="font-normal text-base leading-[24px]">
                        {COMPANY.name}, <br />
                        {COMPANY.addressLines.join(" ")}
                      </p>
                    </li>
                    <li className="flex mb-4  items-center">
                      <a href="tel:+918884414400">
                        <Image
                          width={16}
                          height={21}
                          className=" mr-5"
                          src={IMAGES.phone}
                          alt="Phone icon"
                        />
                      </a>
                      <a href="tel:+918884414400" className="font-normal text-base leading-[24px] text-white">
                        +91 8884414400
                      </a>
                    </li>
                    <li className="flex mb-4 items-center">
                      <a href="mailto:contact@sukuto.com">
                        <Image
                          width={16}
                          height={21}
                          className="mr-5"
                          src={IMAGES.mail}
                          alt="Email icon"
                        />
                      </a>
                      <a href="mailto:contact@sukuto.com" className="font-normal text-base leading-[24px] text-white">
                        contact@sukuto.com
                      </a>
                    </li>
                  </ul>

                  <div className="flex mt-8">
                    <a
                      href="https://x.com/sukutotravel"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Sukuto on X (Twitter)"
                      className="group flex mr-3 items-center justify-center md:w-[30px] md:h-[30px] w-6 h-6 rounded-full bg-cyan-400 hover:bg-white hover:text-black"
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.7433 1.42545C14.2012 1.65977 13.6112 1.82841 13.0034 1.89409C13.6344 1.51853 14.1069 0.925213 14.3323 0.225451C13.7402 0.578331 13.0916 0.825776 12.4152 0.956811C12.1325 0.654018 11.7906 0.4128 11.4107 0.248186C11.0309 0.0835725 10.6213 -0.000908491 10.2075 7.36757e-06C8.5331 7.36757e-06 7.18652 1.35977 7.18652 3.02841C7.18652 3.26273 7.21487 3.49705 7.26093 3.72249C4.7538 3.59113 2.51776 2.39113 1.0312 0.553853C0.760334 1.01737 0.618391 1.54514 0.620138 2.08225C0.620138 3.13314 1.15346 4.05977 1.96672 4.60474C1.48745 4.58583 1.01941 4.45385 0.600648 4.21953V4.25681C0.600648 5.72841 1.63894 6.94793 3.02273 7.22841C2.76291 7.29602 2.49563 7.33061 2.22718 7.33136C2.03051 7.33136 1.84447 7.31184 1.65666 7.28521C2.03937 8.48521 3.15385 9.35681 4.48094 9.38521C3.44265 10.2 2.14214 10.6793 0.729991 10.6793C0.476621 10.6793 0.24274 10.6704 0 10.642C1.3395 11.503 2.92882 12 4.64041 12C10.1968 12 13.2373 7.38817 13.2373 3.38521C13.2373 3.25385 13.2373 3.12249 13.2284 2.99113C13.8167 2.55977 14.3323 2.02545 14.7433 1.42545Z"
                          className="fill-white group-hover:fill-subHeading"
                        />
                      </svg>
                    </a>
                    <a
                      href="https://www.instagram.com/sukutotravel/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Sukuto on Instagram"
                      className="group flex mr-3 items-center justify-center md:w-[30px] md:h-[30px] w-6 h-6 rounded-full bg-cyan-400 hover:bg-white hover:text-black"
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.33253 0.045C5.11862 0.00818181 5.36926 0 7.37167 0C9.37409 0 9.62473 0.00886363 10.4101 0.045C11.1956 0.0811364 11.7317 0.208636 12.2008 0.393409C12.692 0.582273 13.1377 0.8775 13.5062 1.25932C13.8815 1.63364 14.171 2.08636 14.356 2.58682C14.5383 3.06409 14.6629 3.60955 14.6991 4.40727C14.7353 5.20841 14.7433 5.46341 14.7433 7.5C14.7433 9.53727 14.7346 9.79227 14.6991 10.592C14.6636 11.3898 14.5383 11.9352 14.356 12.4125C14.171 12.913 13.881 13.3665 13.5062 13.7414C13.1377 14.1232 12.692 14.4177 12.2008 14.6059C11.7317 14.7914 11.1956 14.9182 10.4115 14.955C9.62472 14.9918 9.37409 15 7.37167 15C5.36926 15 5.11862 14.9911 4.33253 14.955C3.54846 14.9189 3.01233 14.7914 2.54323 14.6059C2.05128 14.4177 1.60556 14.1227 1.2371 13.7414C0.86207 13.3668 0.571851 12.9136 0.386678 12.4132C0.205067 11.9359 0.0804182 11.3905 0.04423 10.5927C0.00804181 9.79159 0 9.53659 0 7.5C0 5.46273 0.00871197 5.20773 0.04423 4.40864C0.0797481 3.60955 0.205067 3.06409 0.386678 2.58682C0.572124 2.08642 0.862565 1.63317 1.23777 1.25864C1.60571 0.87716 2.05098 0.581893 2.54256 0.393409C3.01166 0.208636 3.54779 0.0818182 4.33186 0.045H4.33253ZM10.3505 1.395C9.57312 1.35886 9.33991 1.35136 7.37167 1.35136C5.40344 1.35136 5.17022 1.35886 4.39285 1.395C3.67377 1.42841 3.28375 1.55045 3.02373 1.65341C2.67994 1.78977 2.43399 1.95136 2.17598 2.21386C1.93141 2.45594 1.74319 2.75065 1.62512 3.07636C1.52393 3.34091 1.40397 3.73773 1.37113 4.46932C1.33561 5.26023 1.32824 5.4975 1.32824 7.5C1.32824 9.5025 1.33561 9.73977 1.37113 10.5307C1.40397 11.2623 1.52393 11.6591 1.62512 11.9236C1.74307 12.2489 1.93138 12.5441 2.17598 12.7861C2.41389 13.035 2.70406 13.2266 3.02373 13.3466C3.28375 13.4495 3.67377 13.5716 4.39285 13.605C5.17022 13.6411 5.40277 13.6486 7.37167 13.6486C9.34058 13.6486 9.57312 13.6411 10.3505 13.605C11.0696 13.5716 11.4596 13.4495 11.7196 13.3466C12.0634 13.2102 12.3094 13.0486 12.5674 12.7861C12.812 12.5441 13.0003 12.2489 13.1182 11.9236C13.2194 11.6591 13.3394 11.2623 13.3722 10.5307C13.4077 9.73977 13.4151 9.5025 13.4151 7.5C13.4151 5.4975 13.4077 5.26023 13.3722 4.46932C13.3394 3.73773 13.2194 3.34091 13.1182 3.07636C12.9842 2.72659 12.8254 2.47636 12.5674 2.21386C12.3294 1.96505 12.0398 1.77355 11.7196 1.65341C11.4596 1.55045 11.0696 1.42841 10.3505 1.395ZM6.43011 9.81205C6.95595 10.0347 7.54148 10.0648 8.08668 9.89708C8.63188 9.72936 9.10294 9.37426 9.4194 8.89244C9.73585 8.41062 9.87808 7.83196 9.82178 7.2553C9.76548 6.67864 9.51415 6.13975 9.11072 5.73068C8.85354 5.46919 8.54257 5.26896 8.2002 5.14442C7.85784 5.01987 7.49259 4.97411 7.13075 5.01042C6.76891 5.04673 6.41949 5.16421 6.10764 5.35441C5.79578 5.5446 5.52925 5.80278 5.32724 6.11035C5.12523 6.41793 4.99276 6.76724 4.93937 7.13316C4.88597 7.49907 4.91299 7.87247 5.01846 8.22649C5.12393 8.5805 5.30525 8.90633 5.54934 9.18049C5.79344 9.45466 6.09425 9.67035 6.43011 9.81205ZM4.69241 4.77409C5.04425 4.41612 5.46195 4.13216 5.92166 3.93843C6.38137 3.7447 6.87409 3.64498 7.37167 3.64498C7.86926 3.64498 8.36197 3.7447 8.82168 3.93843C9.28139 4.13216 9.69909 4.41612 10.0509 4.77409C10.4028 5.13206 10.6819 5.55704 10.8723 6.02475C11.0627 6.49246 11.1607 6.99375 11.1607 7.5C11.1607 8.00625 11.0627 8.50754 10.8723 8.97525C10.6819 9.44296 10.4028 9.86794 10.0509 10.2259C9.34036 10.9489 8.37659 11.355 7.37167 11.355C6.36675 11.355 5.40299 10.9489 4.69241 10.2259C3.98182 9.50295 3.58262 8.52241 3.58262 7.5C3.58262 6.47759 3.98182 5.49705 4.69241 4.77409ZM12.0011 4.21909C12.0883 4.13541 12.1581 4.03478 12.2064 3.92316C12.2546 3.81155 12.2804 3.69121 12.2822 3.56927C12.2839 3.44734 12.2616 3.32628 12.2165 3.21328C12.1715 3.10028 12.1046 2.99762 12.0198 2.91139C11.9351 2.82516 11.8342 2.75711 11.7231 2.71126C11.612 2.66542 11.493 2.64271 11.3732 2.64449C11.2533 2.64627 11.1351 2.67249 11.0254 2.72161C10.9157 2.77073 10.8167 2.84175 10.7345 2.93045C10.5745 3.10297 10.487 3.33213 10.4904 3.56927C10.4938 3.80641 10.5879 4.03287 10.7527 4.20057C10.9175 4.36828 11.1401 4.46402 11.3732 4.46748C11.6063 4.47093 11.8315 4.38183 12.0011 4.21909Z"
                          className="fill-white group-hover:fill-subHeading"
                        />
                      </svg>
                    </a>
                    <a
                      href="https://discord.gg/sukuto"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Sukuto on Discord"
                      className="group flex items-center justify-center md:w-[30px] md:h-[30px] w-6 h-6 rounded-full bg-cyan-400 hover:bg-white hover:text-black"
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.4812 0.829529C11.5252 0.439129 10.5164 0.160494 9.48003 0.000569545C9.47064 -0.000837589 9.46099 0.000377705 9.45241 0.00404837C9.44383 0.00771903 9.43674 0.0136648 9.43211 0.021068C9.30219 0.226053 9.15844 0.494173 9.058 0.704078C7.94085 0.553216 6.8045 0.553216 5.68734 0.704078C5.5753 0.470248 5.44856 0.242217 5.3077 0.021068C5.30298 0.0137459 5.29587 0.00786861 5.28732 0.00420974C5.27877 0.000550877 5.26917 -0.000718153 5.25979 0.000569545C4.22316 0.159638 3.21417 0.438418 2.25863 0.829529C2.25045 0.832306 2.24361 0.837522 2.23928 0.844288C0.328192 3.38528 -0.196113 5.86396 0.0609713 8.31148C0.0618928 8.32296 0.0701858 8.33444 0.0803217 8.34182C1.19318 9.07535 2.43793 9.63541 3.76151 9.9981C3.77086 10.0007 3.7809 10.0006 3.79018 9.99783C3.79946 9.99502 3.80749 9.98966 3.81311 9.98252C4.09692 9.63815 4.3494 9.27491 4.56686 8.89282C4.57139 8.88495 4.57294 8.87599 4.57128 8.86727C4.56962 8.85855 4.56484 8.85054 4.55764 8.84444C4.55288 8.84044 4.54723 8.83737 4.54106 8.83542C4.14406 8.69966 3.75957 8.53654 3.39109 8.34756C3.3808 8.34237 3.37314 8.33386 3.36971 8.32378C3.36628 8.31371 3.36734 8.30285 3.37266 8.29344C3.3758 8.28734 3.38054 8.282 3.38648 8.27787C3.46388 8.22621 3.54129 8.17209 3.615 8.11798C3.62151 8.11332 3.62933 8.11032 3.63761 8.10931C3.64588 8.1083 3.65431 8.10931 3.662 8.11224C6.07527 9.09289 8.68758 9.09289 11.0714 8.11224C11.0793 8.10917 11.0881 8.10808 11.0967 8.1091C11.1053 8.11011 11.1134 8.11318 11.1202 8.11798C11.1939 8.17209 11.2713 8.22621 11.3487 8.27787C11.355 8.28194 11.36 8.28734 11.3634 8.29358C11.3667 8.29981 11.3683 8.30669 11.3679 8.31359C11.3676 8.3205 11.3653 8.32722 11.3613 8.33314C11.3573 8.33907 11.3517 8.34402 11.345 8.34756C10.9774 8.53827 10.5924 8.70119 10.1942 8.83461C10.1878 8.83665 10.182 8.83992 10.1773 8.84417C10.1725 8.84842 10.1688 8.85355 10.1665 8.8592C10.1644 8.86468 10.1635 8.87049 10.164 8.87627C10.1645 8.88205 10.1663 8.88768 10.1693 8.89282C10.3904 9.27409 10.6438 9.63815 10.9221 9.98252C10.9277 9.98966 10.9358 9.99502 10.945 9.99783C10.9543 10.0006 10.9644 10.0007 10.9737 9.9981C12.2995 9.63653 13.5463 9.07641 14.6604 8.34182C14.666 8.33838 14.6706 8.33387 14.674 8.32862C14.6773 8.32337 14.6793 8.31751 14.6798 8.31148C14.9875 5.48187 14.1647 3.02369 12.4996 0.845108C12.4979 0.84151 12.4954 0.838264 12.4923 0.835581C12.4891 0.832897 12.4853 0.830836 12.4812 0.829529ZM4.92715 6.82083C4.20012 6.82083 3.6021 6.2272 3.6021 5.49909C3.6021 4.77016 4.18907 4.17652 4.92715 4.17652C5.67076 4.17652 6.26325 4.77508 6.25219 5.49909C6.25219 6.2272 5.66523 6.82083 4.92715 6.82083ZM9.82557 6.82083C9.09947 6.82083 8.50053 6.2272 8.50053 5.49909C8.50053 4.77016 9.08749 4.17652 9.82557 4.17652C10.5692 4.17652 11.1626 4.77508 11.1506 5.49909C11.1506 6.2272 10.5692 6.82083 9.82557 6.82083Z"
                          className="fill-white group-hover:fill-subHeading"
                        />
                      </svg>
                    </a>
                  </div>
                    <div aria-hidden="true" className="pointer-events-none absolute right-[65px] bottom-[18px] h-[107px] w-[110px] bg-[url('/images/circle.png')] bg-no-repeat" />

                </div>
                <div className="md:pl-[30px] md:mt-0 mt-6 z-0 md:w-[60%]">
                  {partnerStatus && (
                    <p
                      className={`pb-4 ${
                        partnerError ? "error-message" : "text-green-500"
                      }`}
                    >
                      {partnerStatus}
                    </p>
                  )}
                  <form onSubmit={handleSubmit(handlePartner)}>
                    <div className="relative">
                      <input
                        id="partner-fullname"
                        type="text"
                        {...register("fullname", {
                          required: "Enter your full name",
                        })}
                        placeholder="Enter your full name."
                        className="border px-3 pl-6 py-1 h-[44px] rounded-full w-full placeholder:text-[12px] focus:outline-none focus-visible:outline-none"
                      />
                      <p className="error-message mb-4">
                        {errors.fullname?.message}
                      </p>
                      <label htmlFor="partner-fullname" className="text-black text-[14px] bg-white absolute left-[33px] top-[-10px] z-10">
                        Full Name*
                      </label>
                    </div>
                    <div className="md:flex md:w-full">
                      <div className="relative md:w-48 md:pr-5">
                        <select
                          id="partner-country"
                          placeholder="Type here"
                          className="border px-3 pl-6 py-1 h-[44px] rounded-full w-full text-[10px] bg-white placeholder:text-[12px] focus:outline-none focus-visible:outline-none"
                          {...register("country", {
                            required: "Please select country",
                          })}
                        >
                          <option value="India">India</option>
                        </select>
                        <p className="error-message mb-4">
                          {errors.country?.message}
                        </p>
                        <label htmlFor="partner-country" className="text-black text-[14px] md:bg-white absolute left-[33px] top-[-10px] z-10">
                          Country
                        </label>
                      </div>

                      <div className="relative w-full">
                        <input
                          id="partner-phone"
                          type="number"
                          maxLength="10"
                          {...register("phoneNumber", {
                            required: "Enter your phone number.",
                            minLength: {
                              value: 10,
                              message:
                                "Please enter a valid 10-digit phone number",
                            },

                            pattern: {
                              value: /^(\+\d{1,3}[- ]?)?\d{10}$/,
                              message: "Invalid phone number",
                            },
                          })}
                          placeholder="Enter your phone number."
                          className="border px-3 pl-6 py-1 h-[44px] w-full rounded-full placeholder:text-[12px] focus:outline-none focus-visible:outline-none"
                        />
                        <p className="error-message mb-4">
                          {errors.phoneNumber?.message}
                        </p>
                        <label htmlFor="partner-phone" className="text-black text-[14px] bg-white absolute left-[33px] top-[-10px] z-10">
                          Phone Number
                        </label>
                      </div>
                    </div>

                    <div className="relative">
                      <input
                        id="partner-city"
                        type="text"
                        {...register("city", {
                          required: "Please enter city name.",
                        })}
                        placeholder="Please enter city name."
                        className="border px-3 pl-6 py-1 h-[44px] rounded-full w-full placeholder:text-[12px] focus:outline-none focus-visible:outline-none"
                      />
                      <p className="error-message mb-4">
                        {errors.city?.message}
                      </p>
                      <label htmlFor="partner-city" className="text-black text-[14px] md:bg-white absolute left-[33px] top-[-10px] z-10">
                        City*
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        id="partner-vehicle-count"
                        type="number"
                        {...register("vehicleCount", {
                          required: "Enter number of vehicles",
                        })}
                        placeholder="Enter number of vehicles you have."
                        className="border px-3 pl-6 py-1 h-[44px] rounded-full w-full placeholder:text-[12px] focus:outline-none focus-visible:outline-none"
                      />
                      <p className="error-message mb-4">
                        {errors.vehicleCount?.message}
                      </p>
                      <label htmlFor="partner-vehicle-count" className="text-black text-[14px] bg-white absolute left-[33px] top-[-10px] z-10">
                        How many vehicles do you have?
                      </label>
                    </div>

                    <button
                      className="rounded-full h-[55px] w-full py-2 bg-primary"
                      type="submit"
                    >
                      <p className="text-center font-medium md:text-2xl text-lg leading-[36px] text-white">
                        Submit Application
                      </p>
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      </main>
      <Footer />
    </>
  );
}
